import { consume } from '@lit/context';
import { localized, t } from '@weavedev/lit-i18next';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import { iconCircleX, iconPlusCircle } from '@/components/icons';
import { global, GlobalComponentType } from '@/context';
import { ANIMATION_SPEED, BREAKPOINT_SMALL_PX } from '@/settings';
import { baseStyles, buttonStyles, fileUploadStyles } from '@/styles';
import { ErrorType, UploadExtensionCheck } from '@/types';
import {
  uploadDragEndHandler,
  uploadDragOverHandler,
  uploadDropHandler,
  uploadInputEvent,
} from '@/utils';

@customElement('fg-upload-file')
@localized()
export class FgUploadFile extends LitElement {
  //How long wil the error message be shown
  @property({ type: Number })
  errorTimer = 5000;

  @property({ type: String })
  uploadType = 'personalisation';

  /**
   * Upload extension check {@link UploadExtensionCheck} just to check if the file extension is valid or not.
   * It can be used as a check for specific file extensions or invalid file extensions.
   *
   * @property {UploadExtensionCheck} filecheck
   */
  @property({ type: String })
  filecheck?: UploadExtensionCheck;

  @state()
  private fileName?: string;

  @state()
  private errorMessage = '';

  @state()
  private file?: File | null;

  @state()
  private isDragging = false;

  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  private fileDropHandler(event: DragEvent) {
    const uploadData = uploadDropHandler(event, this.filecheck);

    this.isDragging = false;

    if (uploadData?.error) {
      this.handleErrorMessage(uploadData.error);
      return;
    }

    if (this.errorMessage) {
      this.errorMessage = '';
    }

    this.file = uploadData?.file;
    this.fileName = uploadData?.file?.name;
  }

  private fileDragOverHandler(event: DragEvent) {
    this.isDragging = uploadDragOverHandler(event);
  }

  private fileDragEndHandler(event: DragEvent) {
    this.isDragging = uploadDragEndHandler(event);
  }

  private inputFile(event: Event) {
    const uploadData = uploadInputEvent(event, this.filecheck);
    if (uploadData?.error) {
      this.handleErrorMessage(uploadData.error);
      return;
    }

    if (this.errorMessage) {
      this.errorMessage = '';
    }

    this.file = uploadData?.file;
    this.fileName = uploadData?.file?.name;
  }

  private resetUploadedFile() {
    this.file = null;
    this.fileName = undefined;
  }

  private handleErrorMessage(error: ErrorType) {
    switch (error) {
      case ErrorType.FILE_SIZE:
        this.errorMessage = t('errors.fileTooLarge');
        break;
      case ErrorType.FILE_EXTENSION:
        this.errorMessage = t('errors.fileWrongExtension', {
          fileExtension: this.filecheck,
        });
        break;
    }

    setTimeout(() => {
      this.errorMessage = '';
    }, this.errorTimer);
  }

  private uploadFile = () => {
    this.dispatchEvent(
      new CustomEvent('upload-file', {
        detail: this.file,
      }),
    );
    this.dispatchEvent(
      new CustomEvent('handle-announcement', {
        composed: true,
        detail: {
          announcement: t('labels.uploadFile'),
        },
      }),
    );
  };

  private openFileDialog() {
    const input = this.shadowRoot?.querySelector(
      '#upload-logo-file',
    ) as HTMLInputElement;
    input.value = '';
    input?.click();
  }

  render() {
    const { layout } = this.globalContext;

    if (!this.file) {
      return html`
        <div
          class=${this.isDragging ? 'drop-area active' : 'drop-area'}
          @drop=${(event: DragEvent) => this.fileDropHandler(event)}
          @dragover=${(event: DragEvent) => this.fileDragOverHandler(event)}
          @dragleave=${(event: DragEvent) => this.fileDragEndHandler(event)}
        >
          <div class="drop-title">
            ${iconPlusCircle}
            ${this.uploadType === 'printexample'
              ? t('labels.dropYourPrintproofHere')
              : t('labels.dropFile')}
          </div>
          <p class="hr-line"><span>${t('labels.or')}</span></p>
          <button class="button button-primary" @click=${this.openFileDialog}>
            ${t('labels.selectFile')}
          </button>
          <input
            id="upload-logo-file"
            type="file"
            @change=${(event: Event) => this.inputFile(event)}
            aria-invalid=${this.errorMessage ? true : false}
            aria-errormessage="error-messages"
            accept=${this.uploadType === 'printexample'
              ? 'application/pdf'
              : ''}
          />
          <span id="error-messages" class="upload-error-message">
            ${this.errorMessage && iconCircleX} ${this.errorMessage ?? nothing}
          </span>
          <p class="upload-note">
            ${this.uploadType === 'printexample'
              ? t('labels.optimalFileFormatPDF')
              : html`
                  ${t(
                    layout === 'retail'
                      ? 'labels.optimalFileFormatsRetail'
                      : 'labels.optimalFileFormatsWholesale',
                  )}
                `}
          </p>
        </div>
      `;
    }
    return html`
      <div
        class="upload-area ${classMap({
          green: this.uploadType === 'printexample',
        })}"
      >
        <span class="file-name">${this.fileName}</span>
        <button
          class="reset-upload-button"
          @click=${() => this.resetUploadedFile()}
        >
          ${t('actions.uploadOtherFile')}
        </button>
        <button class="button button-green" @click=${() => this.uploadFile()}>
          ${t('actions.startUpload')}
        </button>
      </div>
    `;
  }

  static styles = [
    baseStyles,
    buttonStyles,
    fileUploadStyles,
    css`
      .hr-line {
        display: flex;
        flex-direction: row;
        margin-bottom: 18px;
        width: 100%;
      }

      .hr-line span {
        padding: 0 8px;
      }

      .hr-line::before,
      .hr-line::after {
        content: '';
        flex: 1 1;
        border-bottom: 1px solid
          var(--fg-gray--300, var(--fg-fallback-gray--300));
        margin: auto;
      }

      .button.button-green {
        margin-top: 20px;
      }

      input[type='file'] {
        display: none;
      }

      .upload-note {
        display: flex;
        gap: 4px;
        margin-top: 15px;
        text-align: center;
        color: var(--fg-gray--800, var(--fg-fallback-gray--800));
      }

      .file-name {
        font-size: 1rem;
        font-weight: 700;
        color: var(--fg-gray--800, var(--fg-fallback-gray--800));
        text-align: center;
      }

      :host[part='wholesale'] .reset-upload-button {
        color: var(--fg-secondary, var(--fg-fallback-secondary));
      }

      .reset-upload-button {
        display: block;
        appearance: none;
        background-color: transparent;
        margin-top: 5px;
        border: none;
        color: var(--fg-primary, var(--fg-fallback-primary));
        text-decoration: underline;
        cursor: pointer;
      }

      .reset-upload-button:hover {
        color: var(--fg-primary--dark, var(--fg-fallback-primary--dark));
      }

      .upload-error-message {
        visibility: visible;
        opacity: 1;
        display: flex;
        align-items: center;
        background-color: var(
          --fg-error--light,
          var(--fg-fallback-error--light)
        );
        color: var(--fg-error--dark, var(--fg-fallback-error--dark));
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        padding: 8px 16px;
        gap: 8px;
        transition:
          opacity ease-in-out ${ANIMATION_SPEED}s,
          visibility ease-out ${ANIMATION_SPEED}s;
        font-size: 0.875rem;
        font-weight: 700;
      }

      .upload-error-message svg {
        fill: var(--fg-error--dark, var(--fg-fallback-error--dark));
      }

      input[aria-invalid='false'] ~ .upload-error-message {
        visibility: collapse;
        opacity: 0;
      }

      @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
        label.button,
        .button.button-green {
          padding: 0 80px;
        }
      }
    `,
  ];
}
